import React, { useState, useEffect, lazy, Suspense } from 'react';
import Sidebar from './Sidebar';

import TasksView from '../TasksView';
import TablesView from '../TablesView';
import ProjectsView from '../Projects/ProjectsView';
import ConnectionsView from '../ConnectionsView';

import DocumentationView from '../DocumentationView';
import SupportView from '../SupportView';
import KnowledgeBaseView from '../KnowledgeBaseView';
import SettingsView from '../SettingsView';
import WorkspaceSettingsView from '../WorkspaceSettingsView';
import SchedulesView from '../SchedulesView';


import AgentsPage from '../Agents/AgentsPage';
import ChatDock from '../Agents/ChatDock';
import { useChatDock } from '../../contexts/ChatDockContext';
import ChatsPage from '../Chats/ChatsPage';
import InboxView from '../InboxView';
import LogsView from '../LogsView';
import LabCompanion from '../LabCompanion';
import FollowTestController from '../FollowTestController';
import { dataService } from '../../services/dataService';
import { initVersionChecker, stopVersionChecker } from '../../utils/versionChecker';
import { FeatureFlagProvider } from '../../contexts/FeatureFlagContext';
import ErrorBoundary from '../ErrorBoundary';
import Skeleton from '../Skeleton';


import { WorkspaceRoleProvider } from '../../contexts/WorkspaceRoleContext';
// Split out of the main bundle. The whole app was one 1.4MB file, so opening
// the inbox paid for the pipeline canvas, the course material and the charts —
// on a phone, before anything appeared. These are the four heaviest routes and
// the ones least likely to be the first thing anyone opens; reactflow alone is
// a large dependency nobody needs until they look at a canvas.
const PipelineCanvas = lazy(() => import('../Pipeline/PipelineCanvas'));
const UniversityView = lazy(() => import('../UniversityView'));
const LabsView       = lazy(() => import('../LabsView'));
const ReportsView    = lazy(() => import('../ReportsView'));


const PAGE_LABELS = {
  projects:    'Projects',
  tables:      'Data',
  pipeline:    'Pipeline',
  operations:  'Operations',
  agents:      'Agents',
  chats:       'Chats',
  inbox:       'Inbox',
  schedules:   'Schedules',
  tasks:       'Tasks',
  connections: 'Connectors',
  logs:        'Logs',
  reports:     'Reports',
  docs:        'Documentation',
  knowledge:   'Knowledge Base',
  university:  'University',
  labs:        'Labs',
  settings:    'Settings',
  workspaceSettings: 'Workspace Settings',
};

export default function AppLayout({
  user, userData, userAiAccess, onSignOut, onDataChanged
}) {
  const [activePage, setActivePage]         = useState('pipeline');
  const [navTarget, setNavTarget]           = useState(null); // deep-link target for cross-links
  const [workspaces, setWorkspaces]         = useState([]);
  const [workspace, setWorkspace]           = useState(null);
  const [loadingWs, setLoadingWs]           = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem('sidebarCollapsed') === 'true'
  );
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const [companionLab, setCompanionLab]     = useState(null);
  const [updateInfo, setUpdateInfo] = useState(null);
  const { openChat } = useChatDock();

  useEffect(() => {
    initVersionChecker((info) => setUpdateInfo(info));
    return () => stopVersionChecker();
  }, []);

  useEffect(() => { loadWorkspaces(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh the workspace list when something (e.g. the Test App creating or
  // deleting its sandbox) changes the set of workspaces, so the selector stays
  // in sync without a page reload. Current selection is preserved.
  useEffect(() => {
    const onChange = () => loadWorkspaces();
    window.addEventListener('projectly_workspaces_changed', onChange);
    return () => window.removeEventListener('projectly_workspaces_changed', onChange);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadWorkspaces = async () => {
    setLoadingWs(true);
    const list = await dataService.getWorkspaces();
    if (list.length === 0) {
      await dataService.createWorkspace('My Workspace', 'Your first workspace');
      const fresh = await dataService.getWorkspaces();
      setWorkspaces(fresh);
      setWorkspace(fresh[0] || null);
      dataService.setWorkspace(fresh[0]?.id || null);
      if (fresh[0]) localStorage.setItem('activeWorkspaceId', fresh[0].id);
    } else {
      setWorkspaces(list);
      // Preserve the active workspace across reloads (e.g. when the Test App
      // adds/removes its sandbox) so the user is never switched unexpectedly.
      const saved  = localStorage.getItem('activeWorkspaceId');
      const match  = list.find(w => w.id === saved) || list[0];
      setWorkspace(match);
      dataService.setWorkspace(match?.id || null);
      if (match) localStorage.setItem('activeWorkspaceId', match.id);
    }
    setLoadingWs(false);
  };

  const handleWorkspaceChange = (ws) => {
    setWorkspace(ws);
    // dataService resolves project paths relative to the active workspace
    // (workspaces/{id}/projects/...), so it has to be told on every switch —
    // otherwise project reads silently target the previous workspace.
    dataService.setWorkspace(ws?.id || null);
    localStorage.setItem('activeWorkspaceId', ws.id);
  };

  const handleCreateWorkspace = async (name) => {
    await dataService.createWorkspace(name);
    const list = await dataService.getWorkspaces();
    setWorkspaces(list);
    if (list.length > 0) handleWorkspaceChange(list[list.length - 1]);
  };

  const handleDeleteWorkspace = async (id) => {
    await dataService.deleteWorkspace(id);
    const list = await dataService.getWorkspaces();
    setWorkspaces(list);
    if (workspace?.id === id) {
      const next = list[0] || null;
      setWorkspace(next);
      if (next) localStorage.setItem('activeWorkspaceId', next.id);
    }
  };

  const handleToggleCollapse = () => {
    setSidebarCollapsed(c => {
      const next = !c;
      localStorage.setItem('sidebarCollapsed', next);
      return next;
    });
  };

  const handleNavigate = (page, target = null) => {
    setActivePage(page);
    setNavTarget(target); // optional deep-link target (e.g. an article or lab id)
    setMobileSidebarOpen(false);
  };

  // Launch the floating lab companion and jump to the canvas so the user can
  // follow the lab steps hands-on while building.
  const handleStartCompanion = (lab) => {
    setCompanionLab(lab);
    handleNavigate('pipeline');
  };

  const renderPage = () => {
    if (loadingWs) return <div className="pipeline-loading">Loading workspace…</div>;
    switch (activePage) {
      case 'projects':
        return workspace
          ? <ProjectsView workspaceId={workspace.id} user={user} />
          : <div className="pipeline-loading">No workspace selected.</div>;
      case 'tables':
        return workspace
          ? <TablesView workspaceId={workspace.id} onNavigate={handleNavigate} />
          : <div className="pipeline-loading">No workspace selected.</div>;
      case 'pipeline':
        return workspace
          ? <PipelineCanvas workspaceId={workspace.id} />
          : <div className="pipeline-loading">No workspace selected.</div>;
      case 'operations':
        return workspace
          ? <PipelineCanvas workspaceId={workspace.id} opsMode />
          : <div className="pipeline-loading">No workspace selected.</div>;
      case 'agents':
        // Always reachable. Whether agents can actually run depends on the
        // workspace having an API key, and the page says so — which is a more
        // useful answer than a page telling someone to flip a setting they
        // have no reason to know about.
        return <AgentsPage workspaceId={workspace?.id} onNavigate={handleNavigate} />;
      case 'chats':
        return <ChatsPage workspaceId={workspace?.id} onNavigate={handleNavigate} />;
      case 'inbox':
        return <InboxView workspaceId={workspace?.id} workspaces={workspaces} onNavigate={handleNavigate} />;
      case 'logs':
        return <LogsView workspaceId={workspace?.id} />;
      case 'schedules':
        return workspace
          ? <SchedulesView workspaceId={workspace.id} />
          : <div className="pipeline-loading">No workspace selected.</div>;
      case 'tasks':
        return workspace
          ? <TasksView workspaceId={workspace.id} />
          : <div className="pipeline-loading">No workspace selected.</div>;
      case 'connections':
        return <ConnectionsView workspaceId={workspace?.id} />;
      case 'support':
        return <SupportView workspaceId={workspace?.id} workspaceName={workspace?.name} />;
      case 'reports':
        return <ReportsView workspaceId={workspace?.id} />;
      case 'docs':
        return workspace
          ? <DocumentationView workspaceId={workspace.id} />
          : <DocumentationView />;
      case 'knowledge':
        return <KnowledgeBaseView workspaceId={workspace?.id} />;
      case 'university':
        return <UniversityView onNavigate={handleNavigate} initialArticleId={navTarget} />;
      case 'labs':
        return <LabsView workspaceId={workspace?.id} workspaceName={workspace?.name} onNavigate={handleNavigate} onStartCompanion={handleStartCompanion} initialLabId={navTarget} />;
      case 'settings':
        return (
          <div className="settings-page-wrap">
            <SettingsView user={user} userAiAccess={userAiAccess} isAdmin={user?.email === 'chr16.dev@gmail.com' || user?.email === 'chris@splat.sh'} />
          </div>
        );
      case 'workspaceSettings':
        return <WorkspaceSettingsView workspace={workspace} user={user} onWorkspaceUpdated={loadWorkspaces} />;
      default:
        return null;
    }
  };

  return (
    <WorkspaceRoleProvider workspaceId={workspace?.id} userId={user?.uid}>
    <FeatureFlagProvider workspaceId={workspace?.id}>
    <div className="app-layout">
      <Sidebar
        activePage={activePage}
        onNavigate={handleNavigate}
        workspace={workspace}
        workspaces={workspaces}
        onWorkspaceChange={handleWorkspaceChange}
        onCreateWorkspace={handleCreateWorkspace}
        onDeleteWorkspace={handleDeleteWorkspace}
        user={user}
        userData={userData}
        onSignOut={onSignOut}
        collapsed={sidebarCollapsed}
        onToggleCollapse={handleToggleCollapse}
        onBuzzOpen={() => openChat({ id: 'buzz', kind: 'buzz', workspaceId: workspace?.id, user })}
        mobileOpen={mobileSidebarOpen}
        onMobileClose={() => setMobileSidebarOpen(false)}
      />

      <main className="app-main">
        {updateInfo && (
          <div className="update-banner">
            <span className="update-banner-text">
              A new version is available
              {updateInfo.built ? ` — built ${new Date(updateInfo.built).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}` : ''}.
            </span>
            <button className="update-banner-btn" onClick={() => window.location.reload()}>Update now</button>
            <button className="update-banner-dismiss" onClick={() => setUpdateInfo(null)} title="Dismiss">✕</button>
          </div>
        )}
        <div className="mobile-header">
          <button className="mobile-hamburger" onClick={() => setMobileSidebarOpen(true)} aria-label="Open menu">
            ☰
          </button>
          <div className="mobile-header-logo">
            <img src="/logo.png" alt="Projectly" />
            <span className="mobile-header-title">Projectly</span>
          </div>
          <span className="mobile-page-label">{PAGE_LABELS[activePage] || ''}</span>
        </div>
        {/* Around the page, not the whole layout: a page that fails should
            leave the sidebar and the workspace picker usable, so there is a
            way out other than reloading. The `key` resets the boundary when
            you navigate, so one broken page does not poison the next. */}
        <ErrorBoundary key={activePage} label={PAGE_LABELS[activePage] || 'This page'}>
          {/* The pipeline and operations views fetch the canvas chunk, so the
              placeholder is shaped like a canvas; everything else gets the
              generic page outline. */}
          <Suspense fallback={
            <Skeleton
              variant={activePage === 'pipeline' || activePage === 'operations' ? 'canvas' : 'page'}
              label={PAGE_LABELS[activePage] || 'Loading'}
            />
          }>
            {renderPage()}
          </Suspense>
        </ErrorBoundary>
      </main>


      {companionLab && (
        <LabCompanion
          lab={companionLab}
          currentPage={activePage}
          onNavigate={handleNavigate}
          onClose={() => setCompanionLab(null)}
        />
      )}

      {/* Global "Follow test" runner — mounted at the shell so a run survives
          navigation between screens. Started via a window event from Settings. */}
      <FollowTestController />

      {/* Docked above the page, so an agent conversation survives navigating
          between screens — the point is to ask something while you work, not
          instead of working. */}
      <ChatDock workspaceId={workspace?.id} user={user} />
    </div>
    </FeatureFlagProvider>
    </WorkspaceRoleProvider>
  );
}
